import { Controller } from "@hotwired/stimulus"
import { getCloudFrontKeyPairId } from "../vendor/cloudfront-utils.js"

// Connects to data-controller="section-image-loader"
// Loads images from CloudFront CDN for LSAT section forms
// Uses question-images CDN path (different from passage-image-loader)
export default class extends Controller {
  static values = {
    itemUrl: String,
    policy: String,
    signature: String,
    keyPairId: String  // Optional - falls back to meta tag if not provided
  }

  // Get keyPairId from data attribute if provided, otherwise from meta tag
  get keyPairId() {
    return this.hasKeyPairIdValue ? this.keyPairIdValue : getCloudFrontKeyPairId()
  }

  connect() {
    console.log('[section-image-loader] Controller connected', {
      itemUrl: this.itemUrlValue,
      hasPolicy: !!this.policyValue,
      hasSignature: !!this.signatureValue,
      keyPairId: this.keyPairId
    })
    this.loadImage()
  }

  loadImage() {
    if (!this.hasItemUrlValue) {
      console.warn('[section-image-loader] Missing itemUrl value', {
        element: this.element,
        allValues: {
          itemUrl: this.itemUrlValue,
          policy: this.policyValue,
          signature: this.signatureValue,
          keyPairId: this.keyPairId
        }
      })
      return
    }

    console.log('[section-image-loader] Loading image', {
      originalItemUrl: this.itemUrlValue,
      policy: this.policyValue ? `${this.policyValue.substring(0, 20)}...` : 'missing',
      signature: this.signatureValue ? `${this.signatureValue.substring(0, 20)}...` : 'missing',
      keyPairId: this.keyPairId
    })

    // Extract item ID from URL
    // The Location value from Cloudfront.get_transparent_url_components is extracted as _resource[43,30]
    // which gives the path after "https://cdn.lsatengine.com/question-images/" (43 chars)
    // So Location is like "63/63-G1-S.png" (may be truncated to 30 chars max)
    //
    // The HTML element IDs are like "63-G1-S" (for games) or "qid-P" (for reasoning) or "63-C1-P" (for comp)
    // So we need to extract the filename part (after the last slash) and remove .png
    let item
    if (this.itemUrlValue.includes('/')) {
      // If there's a slash, take the filename part (after the last slash)
      const parts = this.itemUrlValue.split('/')
      const filename = parts[parts.length - 1]
      item = filename.replace(".png", "")
      console.log('[section-image-loader] Extracted item ID from path', {
        originalUrl: this.itemUrlValue,
        parts: parts,
        filename: filename,
        extractedItem: item
      })
    } else {
      // If no slash, the Location might just be the filename
      item = this.itemUrlValue.replace(".png", "")
      console.log('[section-image-loader] Extracted item ID from filename', {
        originalUrl: this.itemUrlValue,
        extractedItem: item
      })
    }

    const coverId = item
    const targetId = item + "-alt"
    const cover = document.getElementById(coverId)
    const target = document.getElementById(targetId)

    console.log('[section-image-loader] Looking for elements', {
      coverId: coverId,
      targetId: targetId,
      coverFound: !!cover,
      targetFound: !!target,
      coverElement: cover,
      targetElement: target
    })

    if (!cover || !target) {
      console.error(`[section-image-loader] Could not find elements for item: ${item}`, {
        originalUrl: this.itemUrlValue,
        extractedItem: item,
        coverId: coverId,
        targetId: targetId,
        coverFound: !!cover,
        targetFound: !!target
      })

      // Log available IDs for debugging - look for IDs that might match
      const searchPrefix = item.split('-')[0]
      const allIds = Array.from(document.querySelectorAll('[id]')).map(el => el.id)
      const matchingIds = allIds.filter(id => {
        const idLower = id.toLowerCase()
        const searchLower = searchPrefix.toLowerCase()
        return idLower.includes(searchLower) || idLower.includes(item.split('-').slice(0, 2).join('-').toLowerCase())
      })

      console.warn('[section-image-loader] Available IDs that might match', {
        searchPrefix: searchPrefix,
        totalIdsInDocument: allIds.length,
        matchingIds: matchingIds.slice(0, 20),
        allIdsWithHyphens: allIds.filter(id => id.includes('-')).slice(0, 30)
      })

      // Also check for similar patterns
      const similarPatterns = allIds.filter(id => {
        const parts = id.split('-')
        return parts.length >= 2 && parts[0] === searchPrefix
      })
      if (similarPatterns.length > 0) {
        console.warn('[section-image-loader] IDs with similar pattern', {
          pattern: `${searchPrefix}-*`,
          matches: similarPatterns.slice(0, 20)
        })
      }

      return
    }

    // Build CloudFront signed URL using question-images path
    // The itemUrlValue already contains the path like "63/63-G1-S.png"
    const imageUrl = `https://cdn.lsatengine.com/question-images/${this.itemUrlValue}?Policy=${this.policyValue}&Signature=${this.signatureValue}&Key-Pair-Id=${this.keyPairId}`

    console.log('[section-image-loader] Setting image source', {
      item: item,
      imageUrl: imageUrl.substring(0, 100) + '...',
      targetElement: target,
      targetId: targetId
    })

    target.src = imageUrl

    // Add error handler for image loading
    target.onerror = (error) => {
      console.error('[section-image-loader] Image failed to load', {
        item: item,
        imageUrl: imageUrl,
        error: error,
        targetElement: target
      })
    }

    target.onload = () => {
      console.log('[section-image-loader] Image loaded successfully', {
        item: item,
        imageUrl: imageUrl.substring(0, 100) + '...',
        naturalWidth: target.naturalWidth,
        naturalHeight: target.naturalHeight,
        currentWidth: target.width,
        currentHeight: target.height
      })
    }

    // Note: The original script had onload handler commented out, so we're not setting dimensions here
    // If dimensions need to be set, uncomment and implement similar to other image loaders
  }
}
